import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const deviceLabel = (device, lang) => {
  const d = String(device || '').toLowerCase();
  if (d === 'phone') return lang === 'cs' ? 'Mobil' : 'Mobile';
  if (d === 'tablet') return 'Tablet';
  if (d === 'desktop') return 'Desktop';
  return device;
};

const fmtMs = (v) => (v >= 1000 ? `${(v / 1000).toFixed(2)} s` : `${Math.round(v)} ms`);
const fmtVal = (metric, v) => (metric === 'cls' ? Number(v).toFixed(3) : fmtMs(v));

const statusColor = {
  good: 'text-emerald-400',
  'needs-improvement': 'text-amber-400',
  poor: 'text-red-400'
};

/**
 * "Co řešit první a proč" — ranked (metric × device) priority list.
 * priority = device_share × distance_from_threshold × influenceability
 */
export default function DevicePriorityRanking({ url, onData }) {
  const { language } = useLanguage();
  const lang = language === 'en' ? 'en' : 'cs';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  useEffect(() => {
    if (!url) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const csrfRes = await fetch('/api/csrf-token', { credentials: 'include' });
        const { token: csrfToken } = await csrfRes.json();
        const res = await fetch('/api/lab-audits/device-priority', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrfToken },
          body: JSON.stringify({ url }),
          signal: controller.signal
        });
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !json.success) {
          setError(json.error === 'no_crux_data' ? 'no_crux_data' : (json.message || json.error || 'error'));
          setData(null);
          onDataRef.current?.(null);
        } else {
          setData(json.data);
          onDataRef.current?.(json.data);
        }
      } catch (err) {
        if (!cancelled && err.name !== 'AbortError') {
          setError(err.message || 'error');
          onDataRef.current?.(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; controller.abort(); };
  }, [url]);

  if (loading) {
    return (
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 mb-4">
        <div className="text-sm text-slate-400 animate-pulse">
          {lang === 'cs' ? 'Počítám priority podle zařízení (CrUX)…' : 'Computing device priorities (CrUX)…'}
        </div>
      </div>
    );
  }

  if (error === 'no_crux_data') {
    return (
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 mb-4">
        <div className="text-sm text-slate-400">
          {lang === 'cs'
            ? 'CrUX nemá pro tuto doménu dostatek reálných dat — žebříček priorit podle zařízení nelze sestavit.'
            : 'CrUX does not have enough real-user data for this domain — the device priority ranking cannot be built.'}
        </div>
      </div>
    );
  }

  if (error || !data?.items?.length) return null;

  const actionable = data.items.filter(i => i.score > 0);
  let items = (actionable.length ? actionable : data.items).slice(0, 6);
  // Keep at least one non-INP metric visible (LCP/CLS/TTFB) so the ranking
  // covers loading as well as responsiveness — mirrors the HTML export.
  if (items.length && items.every(i => i.metric === 'inp')) {
    const bestNonInp = data.items.filter(i => i.metric !== 'inp')
      .sort((a, b) => b.score - a.score)[0];
    if (bestNonInp) items = [...items.slice(0, 5), bestNonInp];
  }
  const allGood = actionable.length === 0;
  const maxScore = Math.max(...items.map(i => i.score), 0.001);

  const shareNote = data.shareSource === 'ga4'
    ? (lang === 'cs'
      ? `Podíl zařízení: Google Analytics 4 (${data.ga4?.totalSessions?.toLocaleString('cs-CZ') || '—'} návštěv / 28 dní).`
      : `Device share: Google Analytics 4 (${data.ga4?.totalSessions?.toLocaleString('en-US') || '—'} sessions / 28 days).`)
    : data.shareSource === 'crux'
      ? (lang === 'cs'
        ? 'GA4 není připojeno — podíl zařízení vychází z dat Chrome UX Reportu.'
        : 'GA4 not connected — device share comes from Chrome UX Report data.')
      : (lang === 'cs'
        ? 'Podíl zařízení nebyl k dispozici — použito rovnoměrné rozdělení (orientační).'
        : 'Device share unavailable — equal split used (indicative only).');

  return (
    <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 mb-4">
      <div className="flex items-baseline justify-between gap-3 mb-1 flex-wrap">
        <h4 className="text-sm font-semibold text-white">
          {lang === 'cs' ? 'Co řešit první a proč' : 'What to fix first and why'}
        </h4>
        <span className="text-[11px] text-slate-500 font-mono">
          {lang === 'cs' ? 'priorita = podíl × odstup od prahu × ovlivnitelnost' : 'priority = share × threshold distance × influenceability'}
        </span>
      </div>
      <p className="text-xs text-slate-400 mb-3">
        {allGood
          ? (lang === 'cs'
            ? 'Všechny metriky jsou dle reálných dat CrUX na všech zařízeních v pásmu „dobré" — pořadí níže je jen informativní.'
            : 'All metrics are in the "good" band on every device per real CrUX data — the list below is informational only.')
          : shareNote}
      </p>
      <div className="space-y-2">
        {items.map((it, idx) => {
          const basisNote = it.influenceabilityBasis === 'measured-attribution'
            ? (lang === 'cs' ? 'změřená atribuce' : 'measured attribution')
            : (lang === 'cs' ? 'odhad' : 'estimate');
          return (
            <div key={`${it.metric}-${it.device}`} className="bg-slate-900/50 border border-slate-700/40 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500 font-mono text-xs w-5">{idx + 1}.</span>
                <span className="font-semibold text-white">{it.metric.toUpperCase()}</span>
                <span className="text-slate-400">· {deviceLabel(it.device, lang)}</span>
                <span className={`ml-auto font-mono text-xs ${statusColor[it.status] || 'text-slate-300'}`}>
                  {fmtVal(it.metric, it.p75)}
                </span>
                <span className="font-mono text-xs text-blue-300 w-14 text-right">
                  {it.score.toLocaleString(lang === 'cs' ? 'cs-CZ' : 'en-US', { maximumFractionDigits: 1 })}
                </span>
              </div>
              <div className="h-1 bg-slate-700/50 rounded mt-1.5 mb-1 overflow-hidden">
                <div className="h-full bg-blue-500/70 rounded" style={{ width: `${Math.max(2, (it.score / maxScore) * 100)}%` }} />
              </div>
              <div className="text-[11px] text-slate-500">
                {lang === 'cs'
                  ? `podíl uživatelů ${it.deviceShare.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} % · odstup od prahu ${it.distanceFromThreshold.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })}× · ovlivnitelnost ${Math.round(it.influenceability * 100)} % (${basisNote})`
                  : `user share ${it.deviceShare.toLocaleString('en-US', { maximumFractionDigits: 1 })} % · threshold distance ${it.distanceFromThreshold.toLocaleString('en-US', { maximumFractionDigits: 2 })}× · influenceability ${Math.round(it.influenceability * 100)} % (${basisNote})`}
              </div>
            </div>
          );
        })}
      </div>
      {!allGood && (
        <p className="text-[11px] text-slate-500 mt-2">{shareNote !== '' && allGood === false && data.shareSource !== 'ga4' && data.shareSource !== 'crux' ? shareNote : (lang === 'cs'
          ? 'First-party kód váží víc než skripty třetích stran — čím víc problému máte ve vlastních rukou, tím výš se řadí.'
          : 'First-party code weighs more than third-party scripts — the more of the problem is in your own hands, the higher it ranks.')}</p>
      )}
    </div>
  );
}
